import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink, ChevronLeft, ChevronRight, ChevronsUpDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const PRIORITY_BADGE = {
  "Prioridad alta": "bg-[#D9B300]/20 text-yellow-800 border border-[#D9B300]",
  "Seguimiento": "bg-purple-100 text-purple-800",
  "Observación": "bg-sky-100 text-sky-800",
  "Revisión manual": "bg-rose-100 text-rose-700",
  "Datos limitados": "bg-slate-100 text-slate-600",
};

export const STATUS_BADGE = {
  "Pendiente": "bg-slate-100 text-slate-600",
  "Ver en directo": "bg-sky-100 text-sky-800",
  "Seguimiento": "bg-purple-100 text-purple-800",
  "Citar a prueba": "bg-amber-100 text-amber-800",
  "Fichar": "bg-emerald-100 text-emerald-800",
  "Descartar": "bg-rose-100 text-rose-700",
  "Identidad pendiente": "bg-orange-100 text-orange-700",
};

const Sel = ({ value, onChange, options, placeholder, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 max-w-[150px]"
    data-testid={testid}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const ScoutingPlayersTable = ({
  fixedFilters = {}, defaultSort = "score", showFilters = true, pageSize = 25,
  selectable = false, selected = [], onToggle, deletable = false, persistFilters = false,
}) => {
  const navigate = useNavigate();
  const [facets, setFacets] = useState(null);
  const sp0 = persistFilters ? new URLSearchParams(window.location.search) : null;
  const [q, setQ] = useState(sp0?.get("q") || "");
  const [f, setF] = useState(() => {
    const base = {
      category: "", birth_year: "", position: "", role: "",
      rffm_status: "", priority: "", sporting_status: "", added_by: "",
      min_matches: "", min_starts: "", min_goals: "", min_score: "", team: "",
    };
    if (sp0) Object.keys(base).forEach((k) => { const v = sp0.get(k); if (v) base[k] = v; });
    return base;
  });
  const [sort, setSort] = useState(sp0?.get("sort") || defaultSort);
  const [dir, setDir] = useState(sp0?.get("dir") || "desc");
  const [page, setPage] = useState(Number(sp0?.get("page")) || 1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!persistFilters) return;
    const p = new URLSearchParams(window.location.search);
    const setOrDel = (k, v) => { if (v) p.set(k, v); else p.delete(k); };
    setOrDel("q", q.trim());
    Object.entries(f).forEach(([k, v]) => { if (!(k in fixedFilters)) setOrDel(k, v); });
    setOrDel("sort", sort !== defaultSort ? sort : "");
    setOrDel("dir", dir !== "desc" ? dir : "");
    setOrDel("page", page > 1 ? String(page) : "");
    const qs = p.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, f, sort, dir, page, persistFilters]);

  useEffect(() => {
    api.get("/scouting-market/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ sort, dir, page: String(page), page_size: String(pageSize) });
      if (q.trim()) params.set("q", q.trim());
      Object.entries({ ...f, ...fixedFilters }).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params.set(k, v);
      });
      api.get(`/scouting-market/players?${params.toString()}`)
        .then((r) => setData(r.data))
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, f, sort, dir, page, JSON.stringify(fixedFilters)]);

  const setFilter = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPage(1); };

  const deletePlayer = async (p) => {
    if (!window.confirm(`¿Eliminar definitivamente a "${p.player_name}" del scouting? Se borrarán también sus temporadas y su seguimiento.`)) return;
    try {
      await api.delete(`/scouting-market/players/${p.id}`);
      toast.success(`${p.player_name} eliminado`);
      setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== p.id), total: Math.max(0, d.total - 1) }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const th = (label, key, cls = "") => (
    <th
      className={`px-2 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500 cursor-pointer select-none whitespace-nowrap ${cls}`}
      onClick={() => {
        if (sort === key) setDir(dir === "desc" ? "asc" : "desc");
        else { setSort(key); setDir("desc"); }
        setPage(1);
      }}
    >
      <span className="inline-flex items-center gap-0.5">
        {label} <ChevronsUpDown className={`h-3 w-3 ${sort === key ? "text-purple-700" : "text-slate-300"}`} />
      </span>
    </th>
  );

  return (
    <div className="space-y-3" data-testid="scouting-players-table">
      {showFilters && (
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre, equipo o código RFFM..."
            className="h-9 w-full sm:w-64 text-sm"
            data-testid="scouting-search-input"
          />
          {facets && (
            <>
              {!fixedFilters.category && (
                <Sel value={f.category} onChange={(v) => setFilter("category", v)} options={facets.categories} placeholder="Categoría 26/27" testid="filter-category" />
              )}
              <Sel value={f.birth_year} onChange={(v) => setFilter("birth_year", v)} options={facets.birth_years} placeholder="Año" testid="filter-year" />
              <Sel value={f.position} onChange={(v) => setFilter("position", v)} options={facets.positions} placeholder="Posición" testid="filter-position" />
              <Sel value={f.role} onChange={(v) => setFilter("role", v)} options={facets.roles} placeholder="Rol" testid="filter-role" />
              <Sel value={f.rffm_status} onChange={(v) => setFilter("rffm_status", v)} options={facets.rffm_statuses} placeholder="Estado RFFM" testid="filter-rffm" />
              <Sel value={f.priority} onChange={(v) => setFilter("priority", v)} options={facets.priorities} placeholder="Prioridad" testid="filter-priority" />
              {!fixedFilters.sporting_status && (
                <Sel value={f.sporting_status} onChange={(v) => setFilter("sporting_status", v)} options={facets.sporting_statuses} placeholder="Estado deportivo" testid="filter-status" />
              )}
              {(facets.added_bys || []).length > 0 && (
                <Sel value={f.added_by} onChange={(v) => setFilter("added_by", v)} options={facets.added_bys} placeholder="Añadido por" testid="filter-added-by" />
              )}
              <Input value={f.team} onChange={(e) => setFilter("team", e.target.value)} placeholder="Club" className="h-9 w-28 text-xs" data-testid="filter-team" />
              <Input type="number" value={f.min_matches} onChange={(e) => setFilter("min_matches", e.target.value)} placeholder="PJ ≥" className="h-9 w-20 text-xs" data-testid="filter-min-matches" />
              <Input type="number" value={f.min_goals} onChange={(e) => setFilter("min_goals", e.target.value)} placeholder="Goles ≥" className="h-9 w-20 text-xs" data-testid="filter-min-goals" />
              <Input type="number" value={f.min_score} onChange={(e) => setFilter("min_score", e.target.value)} placeholder="Índice ≥" className="h-9 w-20 text-xs" data-testid="filter-min-score" />
            </>
          )}
        </div>
      )}

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {selectable && <th className="px-2 py-2 w-8"></th>}
                {th("Jugador", "name")}
                {th("Año", "birth_year")}
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">Cat. 26/27</th>
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">Posición</th>
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">Club</th>
                {th("PJ", "matches", "text-right")}
                {th("TIT", "starts", "text-right")}
                {th("G", "goals", "text-right")}
                {th("Índice", "score", "text-right")}
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">Prioridad</th>
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">Estado</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && data.items.length === 0 && (
                <tr><td colSpan={13} className="p-8 text-center text-slate-400 text-sm">Cargando...</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={13} className="p-8 text-center text-slate-400 text-sm">Sin resultados.</td></tr>
              )}
              {data.items.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-purple-50/40 cursor-pointer"
                  onClick={() => navigate(`/scouting/player/${p.id}`)}
                  data-testid={`scouting-player-row-${p.id}`}
                >
                  {selectable && (
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-purple-700 cursor-pointer"
                        checked={selected.some((x) => x.id === p.id)}
                        onChange={() => onToggle?.(p)}
                        data-testid={`compare-check-${p.id}`}
                      />
                    </td>
                  )}
                  <td className="px-2 py-2 font-semibold text-slate-800 whitespace-nowrap max-w-[220px] truncate">{p.player_name}</td>
                  <td className="px-2 py-2 text-slate-600">{p.birth_year || "—"}</td>
                  <td className="px-2 py-2">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 whitespace-nowrap">
                      {p.projected_category_computed || "—"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-600 whitespace-nowrap">{p.position || "—"}</td>
                  <td className="px-2 py-2 text-xs text-slate-600 max-w-[160px] truncate">{p.current_team || p.reported_team || "—"}</td>
                  <td className="px-2 py-2 text-right font-bold">{p.matches_3_seasons ?? "—"}</td>
                  <td className="px-2 py-2 text-right">{p.starts_3_seasons ?? "—"}</td>
                  <td className="px-2 py-2 text-right">{p.goals_3_seasons ?? "—"}</td>
                  <td className="px-2 py-2 text-right font-black text-purple-800">{p.scouting_score ?? "—"}</td>
                  <td className="px-2 py-2">
                    {p.priority && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${PRIORITY_BADGE[p.priority] || "bg-slate-100 text-slate-600"}`}>
                        {p.priority}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_BADGE[p.sporting_status] || "bg-slate-100 text-slate-600"}`}>
                      {p.sporting_status || "Pendiente"}
                    </span>
                  </td>
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2 justify-end">
                      {p.rffm_url && (
                        <a href={p.rffm_url} target="_blank" rel="noreferrer" title="Ver en RFFM" data-testid={`rffm-link-${p.id}`}>
                          <ExternalLink className="h-3.5 w-3.5 text-slate-400 hover:text-purple-700" />
                        </a>
                      )}
                      {deletable && (
                        <button
                          type="button"
                          onClick={() => deletePlayer(p)}
                          title="Eliminar jugador"
                          data-testid={`delete-scouting-player-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <div data-testid="scouting-total">{data.total} jugadores</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="scouting-prev-page">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-bold">{page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="scouting-next-page">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScoutingPlayersTable;
